/**
 * WEB-ATTR-2026-001 — the acquisition model, defined ONCE.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE EXISTS: THE DUPLICATION WAS THE DEFECT SOURCE
 *
 * The capture rules used to live twice — once in `apps/website/src/lib/attribution.ts` for
 * atlas-app.org and once in `apps/atlas/utils/webAcquisition.js` for the app — in two languages
 * with no compiler between them. Eleven declarations were duplicated, and the copies drifted in
 * ways that were each a real misattribution:
 *
 *   · one side read a click id with `??` and the other with `||`, so a Google final-URL template
 *     naming all three ids (`?gclid=&gbraid=ABC`) recorded NO click id on the website — an iOS paid
 *     click filed as direct;
 *   · `isQualifying` accepted seven fields on both clients while the SQL classifier read four, so a
 *     link tagged only `?utm_campaign=…` displaced a stored paid click and then read as `direct`.
 *
 * A 410-line contract guard was written to hold the copies together, and that guard developed two
 * blind spots of its own (an apostrophe inside an inline SQL comment; a regex literal's escaped
 * slash). The guard was treating the symptom. There is now one copy, and the guard's remaining job
 * is only to couple this file to the two places that CANNOT import it: the backend DTO and the SQL.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * IT IS PLAIN ESM JAVASCRIPT ON PURPOSE
 *
 * `apps/website` is excluded from the pnpm workspace, so it cannot depend on a workspace package;
 * the app is bundled by Metro, which resolves a sibling import with no configuration at all. A
 * dependency-free `.js` file inside the app's own tree is the one shape both reach without new
 * plumbing — verified by building both before this was written. Types are JSDoc, so
 * `tsc --noEmit` still checks the website's use of it (`allowJs` is on there).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * IT CAPTURES RAW FIELDS AND NEVER CLASSIFIES
 *
 * There is no "is this Google Ads?" logic here. Channel classification lives in exactly one place,
 * `analytics.web_acquisition_channel()` in Postgres. `isQualifying` below and that function's
 * parameters are the SAME SET by contract, and `scripts/check-web-attribution-contract.mjs`
 * asserts it — a field that qualifies but cannot be classified lets a malformed tag displace a paid
 * click and then read as `direct`.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * PRIVACY: WHAT IS AND IS NOT COLLECTED
 *
 * Collected: the five UTM parameters, a Google click id, the landing HOST and PATH (never the query
 * string), the referrer HOST (never its path or query), an opaque touch id and a timestamp. Never,
 * at any layer: an email, a name, a phone number, an IP address, a user agent, a full referrer URL,
 * or any business record. The landing path comes from `pathname` alone precisely so a query string
 * carrying something personal cannot be swept into storage by accident.
 */

/**
 * The canonical field list. `scripts/check-web-attribution-contract.mjs` reads this array and
 * requires the backend DTO and the migration to declare exactly these.
 *
 * @typedef {'touch_id'|'captured_at'|'gclid'|'utm_source'|'utm_medium'|'utm_campaign'|'utm_term'
 *   |'utm_content'|'landing_host'|'landing_path'|'referrer_host'|'capture_origin'} AttributionField
 */
export const ATTRIBUTION_FIELDS = [
  'touch_id',
  'captured_at',
  'gclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'landing_host',
  'landing_path',
  'referrer_host',
  'capture_origin',
]

/**
 * @typedef {Record<AttributionField, string|null> & {touch_id: string, captured_at: string}} Touch
 * @typedef {{first: Touch, last: Touch}} AttributionState
 */

/** The cookie the marketing site writes and the app reads and writes. Same registrable domain. */
export const ATTRIBUTION_COOKIE = 'atlas_attr'
/** The query parameter carrying the same payload when a cookie is unavailable. */
export const ATTRIBUTION_QUERY_PARAM = 'atlas_attr'

/**
 * How long a stored touch is believed, in both directions.
 *
 * 90 days matches the cookie's own `max-age`, so the two cannot disagree about when attribution
 * ends. The window is SYMMETRICAL: a touch stamped more than this far in the FUTURE is expired too,
 * or a clock that was years fast would freeze that browser's attribution on it permanently.
 */
export const ATTRIBUTION_TTL_DAYS = 90

/**
 * Bound on the serialized payload. A browser caps one cookie near 4096 bytes including its name and
 * attributes, and a cookie that grows without limit is a request-header outage.
 *
 * ⚠️ IT IS SMALLER THAN THE WORST CASE THE FIELD CAPS PERMIT, which is why `serialize` SHEDS rather
 * than returning "". Raising it is not available: two touches at their maximum legal lengths exceed
 * what a cookie can hold at all.
 */
export const MAX_SERIALIZED_BYTES = 3072

/** Per-field caps, mirrored by the backend DTO. Generous, but a URL cannot be used as storage. */
const FIELD_MAX_LENGTH = {
  touch_id: 36,
  captured_at: 32,
  gclid: 512,
  utm_source: 128,
  utm_medium: 128,
  utm_campaign: 128,
  utm_term: 128,
  utm_content: 128,
  landing_host: 128,
  landing_path: 256,
  referrer_host: 128,
  capture_origin: 128,
}

/**
 * Fields shed, all at once, when a payload will not fit — see `serialize`.
 *
 * ⚠️ `referrer_host` IS NOT HERE. It is the sole input the channel function has for
 * `organic_search`, `social` and `referral`, so shedding it would not lose context — it would
 * reclassify the touch as `direct`, the exact conflation this feature exists to remove.
 */
const CONTEXT_FIELDS = ['utm_term', 'utm_content', 'landing_path', 'landing_host', 'capture_origin']

/** Hosts that are OURS wherever this code runs. */
const INTERNAL_HOST_SUFFIXES = ['atlas-app.org', 'qarasay.kz']

/** @param {string|null|undefined} value @param {string} field */
function clamp(value, field) {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  return trimmed.slice(0, FIELD_MAX_LENGTH[field] ?? 128)
}

/** Label-boundary suffix match: `notatlas-app.org` is NOT ours. */
export function hostMatches(host, suffix) {
  if (!host) return false
  const h = String(host).toLowerCase()
  const s = String(suffix).toLowerCase()
  return h === s || h.endsWith(`.${s}`)
}

/**
 * Is this referrer OUR OWN page?
 *
 * ⚠️ THE CURRENT HOST IS PART OF THE ANSWER. Asking only "is it one of the hostnames we hard-coded?"
 * is a question about an ENTITY rather than the PROPERTY that matters, and the two diverge wherever
 * the site is not on its production domain — a preview channel, staging, localhost. There, an
 * internal click-through became `referral: <our own host>`, and because a referrer makes a touch
 * QUALIFYING it also REPLACED the paid last touch. Found by a browser driven against the built site;
 * no unit test could see it, because they all pass hostnames explicitly.
 */
export function isInternalHost(host, currentHost) {
  if (!host) return false
  if (currentHost && (hostMatches(host, currentHost) || hostMatches(currentHost, host))) return true
  return INTERNAL_HOST_SUFFIXES.some((suffix) => hostMatches(host, suffix))
}

/**
 * A v4 UUID. The touch id is an opaque join key and gates nothing — the server write is
 * `on conflict do nothing` into a schema with no HTTP route — so a non-cryptographic fallback is
 * acceptable where `randomUUID` is absent.
 */
export function mintTouchId(cryptoImpl) {
  const c = cryptoImpl ?? (typeof globalThis !== 'undefined' ? globalThis.crypto : undefined)
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  const bytes = new Uint8Array(16)
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes)
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/**
 * Build a touch from a URL and a referrer.
 *
 * ALWAYS returns a touch, including for a bare visit with no campaign parameters and no referrer.
 * `direct` is a real answer: refusing to record it would make "we never measured this visitor" and
 * "this visitor came directly" the same observation, which is how a broken pipeline reports a
 * healthy mix.
 *
 * @param {{href?: string, search?: string, hostname?: string|null, pathname?: string|null,
 *   referrer?: string|null, now?: () => number, mintId?: () => string}} input
 * @returns {Touch|null} null only when `href` is given and unparseable
 */
export function parseTouch(input) {
  let hostname = input.hostname ?? null
  let pathname = input.pathname ?? null
  let params
  if (typeof input.href === 'string') {
    let url
    try {
      url = new URL(input.href)
    } catch {
      return null
    }
    params = url.searchParams
    hostname = url.hostname
    pathname = url.pathname
  } else {
    params = new URLSearchParams(input.search ?? '')
  }

  const now = input.now ? input.now() : Date.now()
  const mint = input.mintId ?? mintTouchId

  let referrerHost = null
  if (input.referrer) {
    try {
      referrerHost = new URL(String(input.referrer)).hostname || null
    } catch {
      referrerHost = null
    }
  }
  if (isInternalHost(referrerHost, hostname)) referrerHost = null

  return {
    touch_id: mint(),
    captured_at: new Date(now).toISOString(),
    // ⚠️ `||`, NOT `??`. A Google Ads final-URL template naming all three click ids has the ones
    // that do not apply substituted with the EMPTY STRING, not dropped — and `??` accepts `""` as a
    // present value, so the real `gbraid` beside it was never read and an iOS paid click was filed
    // as direct. The two clients disagreed about this for one review round, which is exactly the
    // drift that having one copy removes.
    gclid: clamp(params.get('gclid') || params.get('gbraid') || params.get('wbraid'), 'gclid'),
    utm_source: clamp(params.get('utm_source'), 'utm_source'),
    utm_medium: clamp(params.get('utm_medium'), 'utm_medium'),
    utm_campaign: clamp(params.get('utm_campaign'), 'utm_campaign'),
    utm_term: clamp(params.get('utm_term'), 'utm_term'),
    utm_content: clamp(params.get('utm_content'), 'utm_content'),
    landing_host: clamp(hostname, 'landing_host'),
    // PATH ONLY. The query string is deliberately not retained: it is the one part of a URL that
    // can carry something personal, and nothing downstream needs it.
    landing_path: clamp(pathname, 'landing_path'),
    referrer_host: clamp(referrerHost, 'referrer_host'),
    capture_origin: clamp(hostname, 'capture_origin'),
  }
}

/**
 * Does this touch carry acquisition information the channel classifier can NAME?
 *
 * ⚠️ THIS SET AND `analytics.web_acquisition_channel`'s INPUTS ARE THE SAME SET, AND THEY HAVE TO
 * BE. This decides whether a touch may REPLACE the stored `last`; that function decides what
 * channel it then reads as. While they disagreed, a link tagged only `?utm_campaign=weekly`
 * qualified here — displacing a stored paid click — and classified as `direct` there. That is the
 * "a direct visit erases the paid click" failure, through a differently shaped tag.
 *
 * `utm_term` and `utm_content` are deliberately absent: they are the only two no channel rule can
 * name, so a touch carrying one alone would be exactly that mismatch.

 * @param {Touch|null|undefined} touch @returns {boolean}
 */
export function isQualifying(touch) {
  if (!touch) return false
  return Boolean(
    touch.gclid || touch.utm_source || touch.utm_medium || touch.utm_campaign || touch.referrer_host,
  )
}

/** Age in days; `Infinity` when the timestamp will not parse. */
export function touchAgeDays(touch, nowMs) {
  const t = Date.parse(touch?.captured_at ?? '')
  if (Number.isNaN(t)) return Number.POSITIVE_INFINITY
  return (nowMs - t) / 86400000
}

/** Outside the window in EITHER direction — see `ATTRIBUTION_TTL_DAYS`.
 * @param {Touch|null|undefined} touch @param {number} nowMs @param {number} [ttlDays]
 * @returns {boolean}
 */
export function isExpired(touch, nowMs, ttlDays = ATTRIBUTION_TTL_DAYS) {
  if (!touch) return true
  const age = touchAgeDays(touch, nowMs)
  return age > ttlDays || age < -ttlDays
}

/**
 * Fold a touch into state.
 *
 * ⚠️ NO CLOCK COMPARISON ORDERS `last`. Four review rounds each found a real misattribution in one
 * that did — a guard that froze `last` on a fast clock, an escape that let a thirty-day-old touch
 * beat a paid click, a clamp that froze it again, and that clamp handing `last` to the OLDER of two
 * future stamps. Two client-supplied timestamps judged against a client clock have no total order.
 *
 * Ordering is by OBSERVATION instead, and there is nothing left to compare: this is now the only
 * writer of the only copy of the state (the `.atlas-app.org` cookie), so a caller cannot be handed
 * a stale duplicate of state it has already folded. `⛔ Do not reintroduce a timestamp comparison.`
 *
 * `first` is write-once while it is inside the window. When it ages out, a `last` that has NOT is
 * promoted onto it — `first` is write-once so it expires FIRST, and dropping the pair on that
 * boundary erased a non-direct touch still well inside its own window (a paid click on day 89
 * followed by a direct visit on day 91 read as `direct`).

 * @param {AttributionState|null} state
 * @param {Touch|null} touch
 * @param {{now?: number, ttlDays?: number}} [options]
 * @returns {AttributionState}
 */
export function applyTouch(state, touch, { now = Date.now(), ttlDays = ATTRIBUTION_TTL_DAYS } = {}) {
  if (!touch) return state
  if (!state) return { first: touch, last: touch }

  let current = state
  if (isExpired(current.first, now, ttlDays)) {
    if (isExpired(current.last, now, ttlDays)) return { first: touch, last: touch }
    current = { first: current.last, last: current.last }
  }
  if (isQualifying(touch)) return { first: current.first, last: touch }
  return current
}

/** Drop or promote a state whose FIRST touch has aged out. Null when both have.
 * @param {AttributionState|null} state @param {number} nowMs @param {number} [ttlDays]
 * @returns {AttributionState|null}
 */
export function pruneExpired(state, nowMs, ttlDays = ATTRIBUTION_TTL_DAYS) {
  if (!state || !isExpired(state.first, nowMs, ttlDays)) return state
  if (isExpired(state.last, nowMs, ttlDays)) return null
  return { first: state.last, last: state.last }
}

// ── serialization ───────────────────────────────────────────────────────────────────────────────

function base64UrlEncode(json) {
  const b64 =
    typeof btoa === 'function'
      ? btoa(String.fromCharCode(...new TextEncoder().encode(json)))
      : Buffer.from(json, 'utf8').toString('base64')
  return b64.replace(/[+]/g, '-').replace(/[/]/g, '_').replace(/[=]+$/, '')
}

function base64UrlDecode(token) {
  const b64 = String(token).replace(/-/g, '+').replace(/_/g, '/')
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
  if (typeof atob === 'function') {
    const binary = atob(padded)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
    return new TextDecoder().decode(bytes)
  }
  return Buffer.from(padded, 'base64').toString('utf8')
}

/**
 * The shape a `touch_id` must have — the SAME expression the backend DTO's `@Matches` carries.
 *
 * ⚠️ ACCEPTING A LOOSER ID HERE MADE A CORRUPT COOKIE AN UNBOUNDED RETRY LOOP. `pickTouch` checked
 * only that the field was a non-empty string, while the DTO requires a lowercase RFC-4122 UUID — so
 * a crafted or truncated `?atlas_attr=` token carrying `touch_id: "not-a-uuid"` deserialized fine,
 * was folded, was persisted into the SHARED cookie, and was then POSTed and 400'd on every
 * foreground for the life of the install, with a Sentry event each time. `postWebAcquisition`
 * deliberately does not latch on a non-ok status (a transient failure must stay retryable, which is
 * the same reasoning the kill switch relies on), so nothing downstream can stop it. The fix belongs
 * at the boundary that lets the value in.
 *
 * ⚠️ NO `i` FLAG, AND THE FIRST VERSION HAD ONE. The DTO's `@Matches` is case-SENSITIVE — its own
 * message says "lowercase" — so `0AF00000-…-F1` was accepted here and rejected there, leaving the
 * loop this constant exists to close open for the uppercase form. A comment claiming two
 * expressions are the same is worth nothing if they differ by a flag.
 */
const TOUCH_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/

/**
 * The shape a `captured_at` must have — what the DTO's `@IsISO8601()` accepts.
 *
 * ⚠️ `Date.parse` IS NOT THAT CHECK, AND USING IT LEFT THE IDENTICAL HOLE ONE LINE BELOW THE ONE
 * ABOVE. `Date.parse` accepts `"Sep 1 2026 10:00:00 GMT"`, `"2026/09/01 10:00:00"` and
 * `"Tue Sep 01 2026 10:00:00 GMT+0000"`; `@IsISO8601()` rejects all three. Same consequence, in
 * full: the touch is folded, persisted into the SHARED cookie, POSTed, and 400'd on every
 * foreground for the life of the install. Hardening one field of a pair and not the other is how a
 * fix looks complete and is not.
 *
 * Date-only (`2026-09-01`) is accepted because `@IsISO8601()` accepts it; `touchAgeDays` reads it
 * as UTC midnight, which is a real instant and inside the window.
 *
 * ⚠️ EVERY COMPONENT IS BOUNDED, AND EACH LOOSE ONE WAS A SEPARATE WAY INTO THE SAME LOOP.
 * `\d{2}` for the hour accepts `24`, for the minute `60`, for the second `60`, and `[+-]\d{2}:?\d{2}`
 * for the offset `+2400` — all four are rejected by `@IsISO8601()` and all four pass `Date.parse`,
 * so the trailing NaN guard never sees them. Measured: five distinct strings the client stored and
 * the endpoint 400s.
 *
 * ⛔ AND A HAND-PICKED LIST OF BAD VALUES IS WHY THIS FIELD CAME BACK FOUR TIMES. The coupling that
 * matters is not "these six strings are rejected" but "the client is never LOOSER than the
 * validator", and that cannot be asserted here — `class-validator` is a backend dependency. It is
 * asserted in `apps/atlas-backend/.../web-acquisition-client-contract.spec.ts`, which reads THIS
 * expression out of THIS file and sweeps a generated corpus through the real `isISO8601`.
 */
const CAPTURED_AT_RE =
  /^\d{4}-\d{2}-\d{2}([T ]([01]\d|2[0-3]):[0-5]\d(:[0-5]\d(\.\d{1,6})?)?(Z|[+-](0\d|1[0-4]):?[0-5]\d)?)?$/

/**
 * Only the declared fields survive, so nothing can ride along by accident.
 *
 * ⚠️ VALIDATED AFTER THE CLAMP, AND CHECKING THE RAW VALUE FIRST WAS A THIRD ROUND ON THE SAME BUG.
 * `clamp` bounds `captured_at` to 32 code units, so the string that is STORED is not the string
 * that was checked: `2026-09-01T10:00:00.1234567890+05:30` passes the format test, is then
 * truncated to `2026-09-01T10:00:00.1234567890+0`, and THAT is what goes into the shared cookie and
 * onto the wire — where `@IsISO8601()` rejects it, the endpoint 400s, and `postWebAcquisition`
 * (which deliberately never latches a non-ok status) retries daily for the life of the install.
 * A check that reads a different value than the consumer does cannot see what the consumer will.
 */
function pickTouch(value) {
  if (!value || typeof value !== 'object') return null
  if (typeof value.touch_id !== 'string' || typeof value.captured_at !== 'string') return null
  const out = {}
  for (const field of ATTRIBUTION_FIELDS) out[field] = clamp(value[field], field)

  // ⚠️ THE JOIN KEY AND THE CLOCK ARE STORED VERBATIM OR NOT AT ALL. Validating the clamped value
  // closes the "checked one string, stored another" hole above — but on its own it opens a quieter
  // one, because truncation can MAKE a value valid: `<uuid>x` is 37 characters, and clamping it to
  // 36 yields a perfectly well-formed UUID that is not the one the sender wrote. A touch id
  // authorises nothing, so that is not a privilege bug; it is a silently rewritten join key that
  // could attach this device to somebody else's touch row. Same reasoning as `serialize`'s refusal
  // to truncate a gclid: half an identifier still LOOKS like an identifier.
  if (out.touch_id !== value.touch_id.trim()) return null
  if (out.captured_at !== value.captured_at.trim()) return null

  if (!out.touch_id || !TOUCH_ID_RE.test(out.touch_id)) return null
  if (!out.captured_at || !CAPTURED_AT_RE.test(out.captured_at)) return null
  if (Number.isNaN(Date.parse(out.captured_at))) return null
  return out
}

function withoutContext(touch) {
  const out = { ...touch }
  for (const field of CONTEXT_FIELDS) out[field] = null
  return out
}

/**
 * Serialize, SHEDDING rather than abandoning the payload.
 *
 * Returning `""` over the bound was total silent loss: every caller reads it as "nothing to
 * persist", so no cookie, no mirror, and no decorated link — a paid click with long campaign fields
 * dropped in full with nothing reporting it. Truncating is not the alternative: half a gclid still
 * LOOKS like a click id.
 *
 * Three rungs, in the order the contract requires — CONTEXT BEFORE THE LAST TOUCH:
 *   1. `{first, last}` — and `last` is OMITTED whenever it is the same touch, which both decoders
 *      already handle and which halves the common case.
 *   2. drop the CONTEXT fields from both touches, keeping every channel input on each.
 *   3. only then drop the last touch, and finally its context too.
 *
 * ⚠️ RUNG 2 USED TO BE RUNG 3, AND THAT ORDER WROTE A WRONG VALUE INTO THE DATABASE. Dropping
 * `last` makes `deserialize` reconstitute `{first, last: first}`, so the client then posts only
 * `firstTouch`; `lastTouchId` equals `firstTouchId`, the POST latch key changes so nothing
 * suppresses the call, and the server's `on conflict … do update set last_touch_id` MOVES THE
 * STORED LAST TOUCH BACKWARDS ONTO THE FIRST. `last_touch_channel` silently reverts to the
 * first-touch channel and nothing reports it. Shedding `utm_term`/`utm_content`/the landing fields
 * — which no channel rule can read — before shedding a whole touch that carries a `gclid` is the
 * order this file's own priorities already imply. (`linkWebAcquisitionOrganization` refuses the
 * backwards move as well; neither guard is sufficient alone, because rung 3 still exists.)
 *
 * ⚠️ THE BOUND COUNTS UTF-8 BYTES; THE CAPS COUNT UTF-16 CODE UNITS. `clamp` bounds code units
 * while the base64 length follows encoded bytes — two per unit for Cyrillic, this market's ordinary
 * case — so rung 2 runs in production here and is not defensive code. No byte count is quoted in
 * this comment on purpose: it depends entirely on which fields are non-ASCII, three successive
 * reviews each quoted a different fixture's number, and the figures now live in the tests beside
 * the fixture they were measured from.

 * @param {AttributionState} state @returns {string}
 */
export function serialize(state) {
  const encode = (first, last) =>
    base64UrlEncode(
      JSON.stringify(last && last.touch_id !== first.touch_id ? { v: 1, first, last } : { v: 1, first }),
    )

  const full = encode(state.first, state.last)
  if (full.length <= MAX_SERIALIZED_BYTES) return full

  const bothCore = encode(withoutContext(state.first), state.last && withoutContext(state.last))
  if (bothCore.length <= MAX_SERIALIZED_BYTES) return bothCore

  const firstOnly = encode(state.first)
  if (firstOnly.length <= MAX_SERIALIZED_BYTES) return firstOnly

  const core = encode(withoutContext(state.first))
  return core.length <= MAX_SERIALIZED_BYTES ? core : ''
}

/**
 * Parse a payload back into state. Anything malformed reads as "no attribution" — never as a
 * partially-populated touch, which a report would treat as real.

 * @param {string|null|undefined} token @returns {AttributionState|null}
 */
export function deserialize(token) {
  if (!token || typeof token !== 'string' || token.length > MAX_SERIALIZED_BYTES) return null
  try {
    const parsed = JSON.parse(base64UrlDecode(token))
    if (!parsed || typeof parsed !== 'object') return null
    const first = pickTouch(parsed.first)
    if (!first) return null
    return { first, last: pickTouch(parsed.last) || first }
  } catch {
    return null
  }
}

/** Read one cookie out of a `document.cookie` string without a dependency. */
export function readCookie(cookieString, name = ATTRIBUTION_COOKIE) {
  if (!cookieString) return null
  for (const part of String(cookieString).split(';')) {
    const idx = part.indexOf('=')
    if (idx < 0) continue
    if (part.slice(0, idx).trim() !== name) continue
    return part.slice(idx + 1).trim() || null
  }
  return null
}

/**
 * The `domain` that makes the cookie readable on both hosts.
 *
 * `atlas-app.org` and `web.atlas-app.org` share a registrable domain, so a cookie scoped to
 * `.atlas-app.org` is FIRST-PARTY to both and no third-party-cookie policy applies. On a host with
 * no shared parent (localhost, a preview channel) it is written host-only and the query-string
 * handoff carries attribution instead.
 */
export function cookieDomainFor(hostname) {
  if (!hostname) return null
  for (const suffix of INTERNAL_HOST_SUFFIXES) {
    if (hostMatches(hostname, suffix)) return `.${suffix}`
  }
  return null
}

/** @param {string} value @param {{hostname?: string|null, ttlDays?: number, secure?: boolean}} options */
export function buildCookie(value, options = {}) {
  const ttlDays = options.ttlDays ?? ATTRIBUTION_TTL_DAYS
  const domain = cookieDomainFor(options.hostname)
  const parts = [
    `${ATTRIBUTION_COOKIE}=${value}`,
    'Path=/',
    `Max-Age=${Math.floor(ttlDays * 86400)}`,
    // Lax, not Strict: a visitor arriving from a Google ad is a cross-site navigation, and Strict
    // would withhold the cookie on exactly the top-level GET that matters.
    'SameSite=Lax',
  ]
  if (domain) parts.push(`Domain=${domain}`)
  if (options.secure !== false) parts.push('Secure')
  return parts.join('; ')
}

/**
 * Read the one stored state from the carriers.
 *
 * The handoff token is a SEED, not an authority, and reading it as one made it a second copy of the
 * state that could win forever.
 *
 * ⚠️ WHY A BARE "URL FIRST" IS WRONG IN THE APP. `webAcquisition.js` snapshots `location` ONCE at
 * module load — deliberately, so React Navigation rewriting the address bar cannot lose the token —
 * and the bridge then re-resolves on EVERY FOREGROUND. So the token is frozen for the life of the
 * JS process while the cookie keeps moving, and on a till left open for days a second ad click
 * captured meanwhile (in another tab, by the marketing site, into the shared cookie) was overwritten
 * by the stale token seconds later, because the app persists whatever this function returns. That
 * is exactly the "a paid click is silently erased" failure this module exists to prevent, and it is
 * a second copy of the state — the thing the clock-free ordering rule in `applyTouch` depends on
 * NOT existing.
 *
 * ⛔ AND "COOKIE FIRST" IS EQUALLY WRONG, which is why this is not simply reordered. A browser that
 * refuses cookies leaves the token as the ONLY carrier that crossed the hop, and that population is
 * the entire reason the query-string handoff exists.
 *
 * The rule is IDENTITY, not order, and needs no clock: `first` is write-once, so two states that
 * name the same first touch are the SAME LINEAGE — and the stored one is then this device's own
 * write-back of the handoff, folded with everything observed since, i.e. a descendant. A different
 * first touch means a genuinely different acquisition, which only the handoff can be carrying.
 *
 * ⚠️ EVERY CARRIER IS PRUNED BEFORE IT IS COMPARED, AND SKIPPING THAT LET A DEAD LINK DELETE A LIVE
 * PAID CLICK. `deserialize` applies no TTL, so an expired handoff counted as "a different lineage"
 * and won outright — and `resolveWebAcquisition` then pruned the winner to null and PERSISTED that,
 * clearing the cookie and the mirror. A decorated signup link that was bookmarked or shared 200 days
 * ago, opened on a device holding a live 50-day-old paid touch, destroyed it and read as direct.
 * The window is not a display rule; it decides which carriers are evidence at all.

 * @param {{href?: string, cookie?: string|null, mirror?: string|null, now?: number,
 *   ttlDays?: number}} [carriers]
 * @returns {AttributionState|null}
 */
export function readState({ href, cookie, mirror, now = Date.now(), ttlDays = ATTRIBUTION_TTL_DAYS } = {}) {
  const live = (state) => (state ? pruneExpired(state, now, ttlDays) : null)

  const stored = live(deserialize(readCookie(cookie)) ?? deserialize(mirror) ?? null)

  let fromUrl = null
  if (typeof href === 'string') {
    try {
      fromUrl = live(deserialize(new URL(href).searchParams.get(ATTRIBUTION_QUERY_PARAM)))
    } catch {
      /* an unparseable href simply carries no handoff */
    }
  }

  if (!fromUrl) return stored
  if (!stored) return fromUrl
  return stored.first.touch_id === fromUrl.first.touch_id ? stored : fromUrl
}
